// VYV sonic identity — SFX engine.
//
// Two layers, sample-first with a synth fallback:
//  1. SAMPLE layer (preferred): designed assets (public/audio/sfx/*.mp3) are
//     preloaded into a DEDICATED, long-lived AudioContext and played as
//     BufferSource → per-sound GainNode → destination. Fire-and-forget.
//  2. SYNTH fallback: the original synthesized motifs play whenever a sample
//     isn't loaded (asset absent / decode failed) — so the app never goes
//     silent and works before the assets land.
//
// ⚠️ AUDIO-LAYER GUARD RULES (protect the #178 TTS stabilization):
//  1. NEVER play through the TTS shared AudioContext (iosAudioUnlock) or the
//     TTS <audio> element. This engine owns its OWN context, always.
//  2. NEVER call setCategory / setPlaybackMode / getUserMedia /
//     setPreferredSampleRate from here. SFX must not touch the AVAudioSession.
//  3. Don't play SFX DURING TTS speech. mirrorSpeak fires just BEFORE the reply
//     (very short) — ok; no other SFX overlaps the voice.
//  4. The SFX context is LONG-LIVED — created lazily on the first gesture/play
//     and kept alive (never closed per play) → no AVAudioSession churn.
//  5. Gated on `vyv_sfx_enabled` + prefers-reduced-motion; best-effort, never
//     throws, never blocks.

import { Capacitor } from "@capacitor/core";
import { SFX_MANIFEST } from "./sfxManifest";
import { Sentry } from "@/lib/sentry";

const SFX_PREF_KEY = "vyv_sfx_enabled";

/** Whether SFX are enabled (defaults to ON — enabled unless explicitly "false"). */
export function isSfxEnabled(): boolean {
  try {
    return localStorage.getItem(SFX_PREF_KEY) !== "false";
  } catch {
    return true;
  }
}

/** Toggle SFX on/off. Single source of truth for the VYV sonic identity. */
export function setSfxEnabled(value: boolean): void {
  try {
    localStorage.setItem(SFX_PREF_KEY, value ? "true" : "false");
  } catch {
    /* ignore storage failures */
  }
}

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

// ── Dedicated SFX AudioContext (SEPARATE from the TTS getSharedAudioContext) ──
// Long-lived, created lazily on the first play (usually a gesture so it can
// resume), reused for every sample, never closed per play.
let sfxCtx: AudioContext | null = null;
let preloadStarted = false;
const buffers = new Map<string, AudioBuffer>();

function createSfxContext(): AudioContext | null {
  try {
    const w = window as typeof window & { webkitAudioContext?: typeof AudioContext };
    const AudioCtor = w.AudioContext || w.webkitAudioContext;
    if (!AudioCtor) return null;
    // Ask for 48 kHz to match the hardware after #178's setPreferredSampleRate
    // (avoids a resample). Some browsers reject the option arg → bare fallback.
    try {
      return new AudioCtor({ sampleRate: 48000 });
    } catch {
      return new AudioCtor();
    }
  } catch {
    return null;
  }
}

function isNativePlatform(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

// XHR fallback loader for the Capacitor WKWebView. fetch() of bundled assets
// under the capacitor://localhost WKURLSchemeHandler has been observed
// failing on device (prod build 29: bytes verified new via md5 inside
// App.app, web Safari on the SAME iPhone decoded them fine, native fresh
// install fell back to the synth motifs) — XHR with responseType
// "arraybuffer" is the long-standing workaround for that scheme-handler
// path. status 0 is accepted: custom schemes report 0 for success.
function xhrArrayBuffer(url: string): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open("GET", url, true);
      xhr.responseType = "arraybuffer";
      xhr.onload = () => {
        const okStatus = xhr.status === 200 || xhr.status === 0;
        if (okStatus && xhr.response) {
          resolve(xhr.response as ArrayBuffer);
        } else {
          reject(new Error("xhr status " + xhr.status + (xhr.response ? "" : " (empty body)")));
        }
      };
      xhr.onerror = () => reject(new Error("xhr network error"));
      xhr.ontimeout = () => reject(new Error("xhr timeout"));
      xhr.send();
    } catch (e) {
      reject(e);
    }
  });
}

// Bytes loader: fetch first (web-identical path), XHR retry on native only.
// Throws with a reason string so preload can aggregate per-name failures.
async function loadSampleBytes(path: string): Promise<ArrayBuffer> {
  let fetchReason = "";
  try {
    const res = await fetch(path);
    if (res.ok) return await res.arrayBuffer();
    fetchReason = "fetch status " + res.status;
  } catch (e) {
    fetchReason = "fetch threw: " + String((e as Error)?.message ?? e);
  }
  if (!isNativePlatform()) {
    throw new Error(fetchReason);
  }
  // Native retry — absolute URL so the scheme handler resolves it exactly.
  try {
    const absolute = new URL(path, window.location.origin).toString();
    return await xhrArrayBuffer(absolute);
  } catch (e) {
    throw new Error(fetchReason + "; xhr retry: " + String((e as Error)?.message ?? e));
  }
}

// Fetch + decode every manifest asset into the dedicated context. A missing /
// undecodable asset still degrades to the synth fallback for that name, but
// the failure is now LOUD: per-name reasons are aggregated into one
// console.error + one Sentry event. The silent catch{} here is what hid the
// build-29 native regression — every slot was quietly playing the synth
// motifs and nothing reached the logs (CLAUDE.md §11 anti-pattern).
async function preload(ctx: AudioContext): Promise<void> {
  if (preloadStarted) return;
  preloadStarted = true;
  const failures: Record<string, string> = {};
  await Promise.all(
    Object.entries(SFX_MANIFEST).map(async ([name, entry]) => {
      try {
        const arr = await loadSampleBytes(entry.path);
        const buf = await ctx.decodeAudioData(arr);
        buffers.set(name, buf);
      } catch (err) {
        failures[name] = String((err as Error)?.message ?? err);
      }
    }),
  );
  if (Object.keys(failures).length > 0) {
    console.error("[sfx] preload failed for", failures);
    try {
      Sentry.captureMessage("sfx_preload_failed", {
        level: "error",
        tags: { feature: "sfx" },
        extra: {
          failures,
          native: isNativePlatform(),
          loaded: buffers.size,
          expected: Object.keys(SFX_MANIFEST).length,
        },
      });
    } catch {
      // Sentry not initialized (dev) — the console line above stands.
    }
  }
}

// Lazily own the context + kick the one-time preload. Returns null when Web
// Audio is unavailable.
function ensureSfxContext(): AudioContext | null {
  if (sfxCtx) return sfxCtx;
  const ctx = createSfxContext();
  if (!ctx) return null;
  sfxCtx = ctx;
  void preload(ctx);
  return ctx;
}

// Play a preloaded sample. Returns false when it can't (no context / not
// loaded) so the caller falls back to the synth motif.
function playSample(name: string): boolean {
  const ctx = ensureSfxContext();
  if (!ctx) return false;
  const buf = buffers.get(name);
  if (!buf) return false;
  try {
    if (ctx.state === "suspended") void ctx.resume();
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const gain = ctx.createGain();
    gain.gain.value = SFX_MANIFEST[name]?.gain ?? 1;
    src.connect(gain);
    gain.connect(ctx.destination);
    src.start();
    return true;
  } catch {
    return false;
  }
}

// ── Synth fallback engine (original, unchanged) ──────────────────────────────

export type SfxContext = "mirror" | "module";

interface Note {
  freq: number;
  start: number;
  dur: number;
  peak: number;
}

interface Timbre {
  type: OscillatorType;
  lowpass: number | null;
}

const TIMBRES: Record<SfxContext, Timbre> = {
  mirror: { type: "sine", lowpass: 1400 },
  module: { type: "triangle", lowpass: null },
};

/**
 * Synthesize and play a short sequence of notes through the given timbre.
 * FALLBACK ONLY (used when a sample isn't loaded). Self-cleaning: closes its
 * ephemeral context once the tail releases — this is the legacy behavior and is
 * fine for the rare fallback path; the sample path uses the long-lived context.
 */
function render(notes: Note[], context: SfxContext): void {
  if (notes.length === 0) return;
  try {
    const w = window as typeof window & { webkitAudioContext?: typeof AudioContext };
    const AudioCtor = w.AudioContext || w.webkitAudioContext;
    if (!AudioCtor) return;
    const ctx = new AudioCtor();
    if (ctx.state === "suspended") void ctx.resume();

    const timbre = TIMBRES[context];

    let sink: AudioNode = ctx.destination;
    if (timbre.lowpass != null) {
      const filter = ctx.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.value = timbre.lowpass;
      filter.connect(ctx.destination);
      sink = filter;
    }

    let tail = 0;
    for (const n of notes) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = timbre.type;
      osc.frequency.value = n.freq;
      osc.connect(gain);
      gain.connect(sink);

      const t0 = ctx.currentTime + n.start;
      const attack = Math.min(0.015, n.dur * 0.4);
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.linearRampToValueAtTime(n.peak, t0 + attack);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + n.dur);
      osc.start(t0);
      osc.stop(t0 + n.dur + 0.02);
      tail = Math.max(tail, n.start + n.dur);
    }

    const totalMs = (tail + 0.1) * 1000;
    window.setTimeout(() => {
      try {
        void ctx.close();
      } catch {
        /* noop */
      }
    }, totalMs + 60);
  } catch (e) {
    console.error("[sfx] synth fallback failed", e);
  }
}

const VOCAB: Record<"tap" | "confirm" | "success" | "error", Record<SfxContext, Note[]>> = {
  tap: {
    mirror: [{ freq: 440, start: 0, dur: 0.06, peak: 0.12 }],
    module: [{ freq: 880, start: 0, dur: 0.045, peak: 0.1 }],
  },
  confirm: {
    mirror: [
      { freq: 392.0, start: 0, dur: 0.09, peak: 0.14 },
      { freq: 523.25, start: 0.08, dur: 0.11, peak: 0.14 },
    ],
    module: [
      { freq: 659.25, start: 0, dur: 0.07, peak: 0.12 },
      { freq: 987.77, start: 0.07, dur: 0.09, peak: 0.12 },
    ],
  },
  success: {
    mirror: [
      { freq: 392.0, start: 0, dur: 0.1, peak: 0.15 },
      { freq: 493.88, start: 0.09, dur: 0.1, peak: 0.15 },
      { freq: 587.33, start: 0.18, dur: 0.14, peak: 0.15 },
    ],
    module: [
      { freq: 880.0, start: 0, dur: 0.08, peak: 0.13 },
      { freq: 1174.66, start: 0.08, dur: 0.08, peak: 0.13 },
      { freq: 1396.91, start: 0.16, dur: 0.12, peak: 0.13 },
    ],
  },
  error: {
    mirror: [
      { freq: 311.13, start: 0, dur: 0.1, peak: 0.13 },
      { freq: 233.08, start: 0.1, dur: 0.16, peak: 0.13 },
    ],
    module: [
      { freq: 493.88, start: 0, dur: 0.09, peak: 0.12 },
      { freq: 369.99, start: 0.09, dur: 0.14, peak: 0.12 },
    ],
  },
};

const MIRROR_SPEAK: Note[] = [{ freq: 329.63, start: 0, dur: 0.18, peak: 0.07 }];

const SONIC_LOGO: Note[] = [
  { freq: 98.0, start: 0, dur: 0.6, peak: 0.06 },
  { freq: 196.0, start: 0.0, dur: 0.22, peak: 0.12 },
  { freq: 293.66, start: 0.16, dur: 0.24, peak: 0.12 },
  { freq: 392.0, start: 0.32, dur: 0.34, peak: 0.13 },
];

const SONIC_LOGO_SESSION_KEY = "vyv_sonic_logo_played";

function sonicLogoPlayedThisSession(): boolean {
  try {
    return sessionStorage.getItem(SONIC_LOGO_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

function markSonicLogoPlayed(): void {
  try {
    sessionStorage.setItem(SONIC_LOGO_SESSION_KEY, "1");
  } catch {
    /* noop */
  }
}

// ── Public surface ───────────────────────────────────────────────────────────
// Each method gates once (enabled + reduced-motion), then SAMPLE-first, SYNTH
// fallback. Same `sfx.*` API as before; navigate/toggle are new (their synth
// fallback reuses the closest existing motif until their assets land).

/** Eager warm-up the app MAY call from a gesture (optional; play() also inits). */
export function initSfx(): void {
  if (!isSfxEnabled() || prefersReducedMotion()) return;
  const ctx = ensureSfxContext();
  if (ctx && ctx.state === "suspended") void ctx.resume();
}

function emit(sampleName: string, fallback: () => void): void {
  if (!isSfxEnabled() || prefersReducedMotion()) return;
  if (playSample(sampleName)) return;
  fallback();
}

export const sfx = {
  /** Tactile tap on cards / buttons. */
  tap: (context: SfxContext = "module") => emit("tap", () => render(VOCAB.tap[context], context)),
  /** Route change / opening a module. */
  navigate: (context: SfxContext = "module") => emit("navigate", () => render(VOCAB.tap[context], context)),
  /** Save / select confirmation. */
  confirm: (context: SfxContext = "module") => emit("confirm", () => render(VOCAB.confirm[context], context)),
  /** Completion / streak / milestone. */
  success: (context: SfxContext = "module") => emit("success", () => render(VOCAB.success[context], context)),
  /** Error state (rare, soft). */
  error: (context: SfxContext = "module") => emit("error", () => render(VOCAB.error[context], context)),
  /** Switch / toggle flip. */
  toggle: (context: SfxContext = "module") => emit("toggle", () => render(VOCAB.confirm[context], context)),

  /** Soft warm cue as the Mirror begins responding (fires just before speech). */
  mirrorSpeak: () => emit("mirrorSpeak", () => render(MIRROR_SPEAK, "mirror")),

  /** The Mirror "awakening" signature — at most once per browser session. */
  sonicLogo: () => {
    if (sonicLogoPlayedThisSession()) return;
    markSonicLogoPlayed();
    emit("mirrorWake", () => render(SONIC_LOGO, "mirror"));
  },
};
